import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Input } from '../common/Input'
import { Button } from '../common/Button'
import { StepHeader } from './StepHeader'

interface CallInitiationStepProps {
  onNext: (callId: string) => void
}

/**
 * Format phone number as user types: (XXX)XXX-XXXX
 */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  const limited = digits.substring(0, 10)

  if (limited.length === 0) return ''
  if (limited.length <= 3) return `(${limited}`
  if (limited.length <= 6) return `(${limited.slice(0, 3)})${limited.slice(3)}`
  return `(${limited.slice(0, 3)})${limited.slice(3, 6)}-${limited.slice(6)}`
}

/**
 * Call initiation step - caregiver enters phone number and clicks "Call Me Now"
 */
export function CallInitiationStep({ onNext }: CallInitiationStepProps) {
  const { t } = useTranslation()
  const [phone, setPhone] = useState('')
  const [error, setError] = useState('')
  const [isInitiating, setIsInitiating] = useState(false)

  const initiateCall = useMutation(api.calls.initiateOnboardingCall)

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhone(formatted)
    if (error) setError('')
  }

  const handleCallNow = async () => {
    // Extract digits for validation
    const digits = phone.replace(/\D/g, '')

    if (digits.length === 0) {
      setError(t('errors.required'))
      return
    }

    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit US phone number')
      return
    }

    // Format as E.164 for storage: +1XXXXXXXXXX
    const e164 = `+1${digits}`

    try {
      setIsInitiating(true)
      const result = await initiateCall({
        caregiverPhone: e164,
      })
      onNext(result.callId)
    } catch {
      setError('Failed to initiate call. Please try again.')
      setIsInitiating(false)
    }
  }

  return (
    <div>
      <StepHeader type="caregiver" title={t('voiceOnboarding.title')} />

      <div
        style={{
          marginBottom: '1.5rem',
          padding: '16px',
          backgroundColor: '#f0f9ff',
          borderRadius: '12px',
          fontSize: '15px',
          color: '#0369a1',
          lineHeight: '1.6',
        }}
      >
        <p style={{ margin: 0 }}>{t('voiceOnboarding.explanation')}</p>
      </div>

      <Input
        label={t('voiceOnboarding.phoneLabel')}
        placeholder={t('voiceOnboarding.phonePlaceholder')}
        type="tel"
        value={phone}
        onChange={handlePhoneChange}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleCallNow()
          }
        }}
        error={error}
        helpText="We'll call you immediately to help set up your account"
        prefix="+1"
        autoFocus
      />

      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <Button onClick={handleCallNow} disabled={isInitiating} style={{ flex: 1 }}>
          {isInitiating ? 'Initiating Call...' : t('voiceOnboarding.callButton')}
        </Button>
      </div>
    </div>
  )
}
